import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';

interface PlayerRecord {
    id: string;
    gamerTag: string;
    game: string;
    main?: string;
    alts?: string;
    bestResult?: string;
    season: string;
}

interface EventResult {
    id: string;
    eventName: string;
    eventDate: string;
    location: string;
    game: string;
    placement: string;
    players?: string;
}

export default function AdminRecordsPage() {
    const [activeTab, setActiveTab] = useState<'players' | 'events'>('players');
    const [players, setPlayers] = useState<PlayerRecord[]>([]);
    const [events, setEvents] = useState<EventResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<PlayerRecord & EventResult>>({});

    // Load data
    useEffect(() => {
        async function loadData() {
            try {
                const [playersSnap, eventsSnap] = await Promise.all([
                    getDocs(collection(db, 'playerRecords')),
                    getDocs(collection(db, 'eventResults'))
                ]);
                setPlayers(playersSnap.docs.map(d => ({ id: d.id, ...d.data() } as PlayerRecord)));
                setEvents(eventsSnap.docs.map(d => ({ id: d.id, ...d.data() } as EventResult)));
            } catch (err) {
                console.error('Error loading data:', err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    const savePlayer = async () => {
        if (!editForm.gamerTag) return;
        const id = editing === 'new' ? `player-${Date.now()}` : editing!;
        try {
            await setDoc(doc(db, 'playerRecords', id), {
                gamerTag: editForm.gamerTag,
                game: editForm.game || '',
                main: editForm.main || '',
                alts: editForm.alts || '',
                bestResult: editForm.bestResult || '',
                season: editForm.season || 'Fall 2025',
            });
            // Refresh
            const snap = await getDocs(collection(db, 'playerRecords'));
            setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() } as PlayerRecord)));
            setEditing(null);
            setEditForm({});
        } catch (err) {
            console.error('Error saving:', err);
        }
    };

    const saveEvent = async () => {
        if (!editForm.eventName) return;
        const id = editing === 'new' ? `event-${Date.now()}` : editing!;
        try {
            await setDoc(doc(db, 'eventResults', id), {
                eventName: editForm.eventName,
                eventDate: editForm.eventDate || '',
                location: editForm.location || '',
                game: editForm.game || '',
                placement: editForm.placement || '',
                players: editForm.players || '',
            });
            const snap = await getDocs(collection(db, 'eventResults'));
            setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() } as EventResult)));
            setEditing(null);
            setEditForm({});
        } catch (err) {
            console.error('Error saving:', err);
        }
    };

    const deleteRecord = async (type: 'player' | 'event', id: string) => {
        if (!confirm('Delete this record?')) return;
        try {
            await deleteDoc(doc(db, type === 'player' ? 'playerRecords' : 'eventResults', id));
            if (type === 'player') {
                setPlayers(players.filter(p => p.id !== id));
            } else {
                setEvents(events.filter(e => e.id !== id));
            }
        } catch (err) {
            console.error('Error deleting:', err);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-display font-bold text-white">Records & Stats Editor</h1>
                <p className="text-secondary-400">Manage player records and event results</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveTab('players')}
                    className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'players' ? 'bg-primary-500 text-white' : 'bg-secondary-800 text-secondary-300'
                        }`}
                >
                    Player Records
                </button>
                <button
                    onClick={() => setActiveTab('events')}
                    className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'events' ? 'bg-primary-500 text-white' : 'bg-secondary-800 text-secondary-300'
                        }`}
                >
                    Event Results
                </button>
            </div>

            {/* Player Records */}
            {activeTab === 'players' && (
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-white">Player Records</h2>
                        <button
                            onClick={() => { setEditing('new'); setEditForm({ season: 'Fall 2025' }); }}
                            className="btn-primary text-sm"
                        >
                            + Add Player
                        </button>
                    </div>

                    {editing && activeTab === 'players' && (
                        <div className="bg-secondary-800 p-4 rounded-lg mb-4">
                            <div className="grid md:grid-cols-3 gap-3">
                                <input
                                    placeholder="Gamer Tag"
                                    value={editForm.gamerTag || ''}
                                    onChange={e => setEditForm({ ...editForm, gamerTag: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Game (SSBU, MK8, etc.)"
                                    value={editForm.game || ''}
                                    onChange={e => setEditForm({ ...editForm, game: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Main Character"
                                    value={editForm.main || ''}
                                    onChange={e => setEditForm({ ...editForm, main: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Alt Characters (comma sep)"
                                    value={editForm.alts || ''}
                                    onChange={e => setEditForm({ ...editForm, alts: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Best Result"
                                    value={editForm.bestResult || ''}
                                    onChange={e => setEditForm({ ...editForm, bestResult: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Season"
                                    value={editForm.season || ''}
                                    onChange={e => setEditForm({ ...editForm, season: e.target.value })}
                                    className="input"
                                />
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={savePlayer} className="btn-primary text-sm">Save</button>
                                <button onClick={() => { setEditing(null); setEditForm({}); }} className="btn-secondary text-sm">Cancel</button>
                            </div>
                        </div>
                    )}

                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-secondary-700 text-left">
                                <th className="py-2 text-secondary-400">Gamer Tag</th>
                                <th className="py-2 text-secondary-400">Game</th>
                                <th className="py-2 text-secondary-400">Main</th>
                                <th className="py-2 text-secondary-400">Best Result</th>
                                <th className="py-2 text-secondary-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.map(p => (
                                <tr key={p.id} className="border-b border-secondary-800">
                                    <td className="py-2 text-white">{p.gamerTag}</td>
                                    <td className="py-2 text-secondary-300">{p.game}</td>
                                    <td className="py-2 text-secondary-300">{p.main}</td>
                                    <td className="py-2 text-secondary-400">{p.bestResult}</td>
                                    <td className="py-2">
                                        <button onClick={() => { setEditing(p.id); setEditForm(p); }} className="text-primary-400 mr-2">Edit</button>
                                        <button onClick={() => deleteRecord('player', p.id)} className="text-red-400">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Event Results */}
            {activeTab === 'events' && (
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-white">Event Results</h2>
                        <button
                            onClick={() => { setEditing('new'); setEditForm({}); }}
                            className="btn-primary text-sm"
                        >
                            + Add Result
                        </button>
                    </div>

                    {editing && activeTab === 'events' && (
                        <div className="bg-secondary-800 p-4 rounded-lg mb-4">
                            <div className="grid md:grid-cols-3 gap-3">
                                <input
                                    placeholder="Event Name"
                                    value={editForm.eventName || ''}
                                    onChange={e => setEditForm({ ...editForm, eventName: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Date"
                                    value={editForm.eventDate || ''}
                                    onChange={e => setEditForm({ ...editForm, eventDate: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Location"
                                    value={editForm.location || ''}
                                    onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Game"
                                    value={editForm.game || ''}
                                    onChange={e => setEditForm({ ...editForm, game: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Placement"
                                    value={editForm.placement || ''}
                                    onChange={e => setEditForm({ ...editForm, placement: e.target.value })}
                                    className="input"
                                />
                                <input
                                    placeholder="Players (comma sep)"
                                    value={editForm.players || ''}
                                    onChange={e => setEditForm({ ...editForm, players: e.target.value })}
                                    className="input"
                                />
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={saveEvent} className="btn-primary text-sm">Save</button>
                                <button onClick={() => { setEditing(null); setEditForm({}); }} className="btn-secondary text-sm">Cancel</button>
                            </div>
                        </div>
                    )}

                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-secondary-700 text-left">
                                <th className="py-2 text-secondary-400">Event</th>
                                <th className="py-2 text-secondary-400">Date</th>
                                <th className="py-2 text-secondary-400">Game</th>
                                <th className="py-2 text-secondary-400">Placement</th>
                                <th className="py-2 text-secondary-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map(e => (
                                <tr key={e.id} className="border-b border-secondary-800">
                                    <td className="py-2 text-white">{e.eventName}</td>
                                    <td className="py-2 text-secondary-300">{e.eventDate}</td>
                                    <td className="py-2 text-secondary-300">{e.game}</td>
                                    <td className="py-2 text-secondary-300">{e.placement}</td>
                                    <td className="py-2">
                                        <button onClick={() => { setEditing(e.id); setEditForm(e); }} className="text-primary-400 mr-2">Edit</button>
                                        <button onClick={() => deleteRecord('event', e.id)} className="text-red-400">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
